import { z } from 'zod';
import { EpochMsSchema, UlidSchema } from './common';

/** Dotted event type from the taxonomy (docs/02-specs.md §19), or '*' for everything. */
export const WebhookEventTypeSchema = z.union([
  z.literal('*'),
  z.string().regex(/^[a-z_]+(\.[a-z_]+)+$/, 'dotted lowercase, e.g. application.submit'),
]);

export const WebhookCreateSchema = z.object({
  url: z.string().url(),
  events: z.array(WebhookEventTypeSchema).min(1).max(50),
  orgId: UlidSchema.nullish(),
  description: z.string().max(280).default(''),
});

export const WebhookUpdateSchema = z
  .object({
    url: z.string().url(),
    events: z.array(WebhookEventTypeSchema).min(1).max(50),
    description: z.string().max(280),
    active: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');

export const WebhookSchema = z.object({
  id: UlidSchema,
  url: z.string(),
  events: z.array(WebhookEventTypeSchema),
  orgId: UlidSchema.nullable(),
  description: z.string(),
  active: z.boolean(),
  lastStatus: z.number().int().nullable(),
  lastDeliveryAt: EpochMsSchema.nullable(),
  failureCount: z.number().int().nonnegative(),
  createdAt: EpochMsSchema,
});
export const WebhookListSchema = z.object({ items: z.array(WebhookSchema) });

export const WebhookCreatedSchema = WebhookSchema.extend({
  secret: z.string().describe('Shown once, at creation and rotation only.'),
});

export const WebhookIdParamsSchema = z.object({ id: UlidSchema });

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'success', 'failed', 'exhausted'] as const;
export const WebhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);

export const WebhookDeliverySchema = z.object({
  id: UlidSchema,
  webhookId: UlidSchema,
  eventType: z.string(),
  attempt: z.number().int().positive(),
  status: WebhookDeliveryStatusSchema,
  responseStatus: z.number().int().nullable(),
  responseSnippet: z.string().nullable(),
  createdAt: EpochMsSchema,
  deliveredAt: EpochMsSchema.nullable(),
  nextAttemptAt: EpochMsSchema.nullable(),
});
export const WebhookDeliveryListSchema = z.object({ items: z.array(WebhookDeliverySchema) });
export const WebhookDeliveryParamsSchema = z.object({ id: UlidSchema, deliveryId: UlidSchema });
